import { Button, Dialog, Grid, Slide } from '@material-ui/core';
import PreviewMode from 'components/Shared/PreviewMode';
import { SvgIcon } from 'components/Shared/SvgIcon';
import { WithStyles } from 'decorators/withStyles';
import { enPreviewMode } from 'interfaces/previewMode';
import { enCustomizationForm, enTheme } from 'interfaces/theme';
import { PureComponent } from 'react';
import * as React from 'react';
import { interval } from 'rxjs';
import * as rxjsOperators from 'rxjs-operators';
import themeCustomizerService from 'services/themeCustomizer';
import { SUN_URL } from 'settings';

import { productNewService } from '../../../services/productNewService';
import CustomizerAppBar from './CustomizerAppBar';
import CustomizerDrawer from './CustomizerDrawer';
import ToastPreview from './CustomizerDrawer/CustomizerOptionForm/Forms/ToastPreview';
import WidgetInstructions from './WidgetInstructions';

interface IState {
  openCustomizer: boolean;
  previewMode: enPreviewMode;
  previewCustomization: boolean;
  themeId: enTheme;
  showToastPreview: boolean;
  id: number;
}

interface IProps {
  classes?: any;
}

@WithStyles(theme => ({
  preview: {
    border: 'none',
    height: 'calc(100vh - 64px)',
    width: '100%'
  },
  mobile: {
    width: '30%'
  },
  iframePreviewMode: {
    width: '100%',
    position: 'relative'
  },
  previewModeGrid: {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: theme.variables.colors['customizerTitle']
  },
  previewMode: {
    position: 'relative',
    zIndex: 1
  },
  customizationPreview: {
    position: 'absolute',
    right: 0,
    margin: `${theme.spacing.unit}px ${theme.spacing.unit * 3}px`,
    display: 'flex',
    width: 320,
    justifyContent: 'space-between'
  },
  widget: {
    marginRight: '18.5%'
  },
  previewButton: {
    background: theme.variables.colors['title'],
    color: '#FFF',
    padding: '10px 44px',
    textTransform: 'none',
    zIndex: 1,
    '& svg': {
      width: 25,
      marginRight: 4
    },
    '&:hover': {
      background: theme.variables.colors['customizerTitle']
    }
  },
  hidden: {
    display: 'none'
  },
  show: {
    overflow: 'scroll',
    height: 'calc(100vh - 64px)'
  },
  instructions: {
    padding: theme.spacing.unit * 2
  },
  instructionsTitle: {
    fontSize: 24
  },
  step: {
    paddingTop: 16
  }
}))
export default class ThemeCustomizer extends PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      openCustomizer: false,
      previewCustomization: false,
      previewMode: enPreviewMode.DESKTOP,
      themeId: null,
      showToastPreview: false,
      id: 33345
    };
  }

  componentDidMount() {
    themeCustomizerService
      .shouldOpenCustomizer()
      .pipe(
        rxjsOperators.combineLatest(themeCustomizerService.getThemeId(), productNewService.getSelectedProduct()),
        rxjsOperators.logError(),
        rxjsOperators.filter(([, , selectedProduct]) => !!selectedProduct),
        rxjsOperators.bindComponent(this)
      )
      .subscribe(([openCustomizer, themeId, selectedProduct]) => {
        this.setState(oldState => {
          return { ...oldState, openCustomizer, themeId, id: selectedProduct.id };
        });
      });

    interval(1000)
      .pipe(
        rxjsOperators.filter(() => !!document.getElementById('themePreview')),
        rxjsOperators.switchMap(() => themeCustomizerService.themePostMessage()),
        rxjsOperators.combineLatest(themeCustomizerService.getSelectedOption()),
        rxjsOperators.logError(),
        rxjsOperators.bindComponent(this)
      )
      .subscribe(([themeMessage, selectedOption]) => {
        const iframe = (window as any).frames.themePreview;

        const data = { selectedOption, ...themeMessage };

        if (iframe) {
          iframe.postMessage('template:' + JSON.stringify(data), '*');
        }
      });

    themeCustomizerService
      .getSelectedOption()
      .pipe(rxjsOperators.logError(), rxjsOperators.bindComponent(this))
      .subscribe(option => {
        if (!option) {
          this.setState({ showToastPreview: false });
          return;
        }

        this.setState({ showToastPreview: option.id === enCustomizationForm.ToastMessages });
      });
  }

  handleChangePreviewMode = (previewMode: enPreviewMode) => {
    this.setState({ previewMode });
  };

  handlePreviewCustomization = () => {
    this.setState(oldState => {
      return {
        ...oldState,
        previewCustomization: !oldState.previewCustomization
      };
    });
  };

  render() {
    const { openCustomizer, previewMode, previewCustomization, themeId, showToastPreview, id } = this.state;
    const { classes } = this.props;

    const previewGridSize = previewCustomization ? 12 : themeId === enTheme.WIDGET ? 8 : 10;

    if (!openCustomizer) {
      return null;
    }

    return (
      <Dialog
        fullScreen
        disableBackdropClick
        disableEscapeKeyDown
        open={openCustomizer}
        TransitionComponent={Transition}
      >
        <CustomizerAppBar />
        <Grid container direction="row" spacing={0}>
          <Grid item xs={2} className={previewCustomization ? classes.hidden : classes.show}>
            <CustomizerDrawer />
          </Grid>
          <Grid item xs={previewGridSize} className={classes.previewModeGrid}>
            <div
              className={`${classes.customizationPreview} ${
                themeId === enTheme.WIDGET && !previewCustomization ? classes.widget : ''
              }`}
            >
              {themeId !== enTheme.WIDGET && (
                <div className={classes.previewMode}>
                  <PreviewMode onChangePreviewMode={this.handleChangePreviewMode} />
                </div>
              )}
              <Button variant="contained" className={classes.previewButton} onClick={this.handlePreviewCustomization}>
                <SvgIcon name={previewCustomization ? 'previewOff' : 'previewOn'} />
                Preview
              </Button>
            </div>
            {/* tslint:disable-next-line:max-line-length */}
            <iframe
              id="themePreview"
              name="themePreview"
              src={`${SUN_URL}/${id}?templateId=${themeId}&nolog=1`}
              className={`${classes.preview} ${previewMode === enPreviewMode.MOBILE ? classes.mobile : ''} ${
                !previewCustomization && previewMode === enPreviewMode.DESKTOP ? classes.iframePreviewMode : ''
              }`}
            >
              &nbsp;
            </iframe>
          </Grid>

          {themeId === enTheme.WIDGET ? (
            <Grid item xs={2}>
              <WidgetInstructions />
            </Grid>
          ) : null}

          {showToastPreview && <ToastPreview />}
        </Grid>
      </Dialog>
    );
  }
}

function Transition(props: any) {
  return <Slide direction="up" {...props} />;
}
